import CriticalCard from './criticalCard';
import { useFetchTasks } from '../../hooks/useFetchTaskList';
import { IFilterOption } from '../../interface/transaction';
import { useEffect, useMemo, useState } from 'react';
import { IGroupedActivities, ITask } from '../../interface/tasks';
import { useTranslation } from 'react-i18next';
import { TasksFilterValues } from '../../constants/tasks';
import dayjs from 'dayjs';
import {
  calculateDuration,
  formatter,
  getDisplayKey,
  getStatusFilterForTasks,
} from '../../constants/utilityFunctions';
import { ROUTES, SystemConstants } from '../../constants/constants';
import { useQueryClient } from '@tanstack/react-query';
import { Spin } from '../Ant';
import CalendarView from '../task/calendar';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { useTask } from '../../context/TaskContext';
import NoData from '../dashboard/noData';
import { DangerEmpty } from '../icons';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useScrollToDate } from '../../hooks/useScrollToDate';
import { useSearchParams } from 'react-router-dom';
import { IActiveSortState, ISortOption } from '../../interface/common';
import SortSelector from '../sortPopover/sortSelector';
import SkeletonGroupedCardList from '../common/skeleton/skeletonGroupedCardList';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';
import CollapsibleDateGroup from '../collapsibleDateGroup/collapsibleDateGroup';
import {
  DateGroupList,
  toDateGroupKey,
  useCollapsedDateGroups,
} from '../../hooks/useCollapsedDateGroups';
import { useFetchAllPages } from '../../hooks/useFetchAllPages';

const CriticalTaskLayout = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { push } = useNavigationStack();
  const { updateTask, clearInitialTask } = useTask();
  const { getDateRef, scrollToDate } = useScrollToDate();
  const criticalTaskFilterOptions: IFilterOption[] = [
    { label: t('task:filter.allCriticalTasks'), value: TasksFilterValues.allTasks },
    { label: t('task:filter.completedCriticalTasks'), value: TasksFilterValues.completed },
    { label: t('task:filter.incompleteCriticalTasks'), value: TasksFilterValues.inComplete },
  ];
  const calendarActiveSort: IActiveSortState = { field: 'dueTime', direction: 'ASC' };
  const calendarSortOptions: ISortOption[] = useMemo(
    () => [{ field: 'dueTime', label: t('task:dueDate'), type: 'date', defaultDirection: 'ASC' }],
    [t],
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('transactionSearch') || '';
  const initialFilterValue = searchParams.get('transactionFilter');
  const initialFilter = criticalTaskFilterOptions.find((opt) => opt.value === initialFilterValue);
  const initialSelectedDate = searchParams.get('selectedDate');
  const initialStartDate = searchParams.get('startDate');
  const initialEndDate = searchParams.get('endDate');

  const parsedInitialStartDate = initialStartDate
    ? dayjs(initialStartDate.replace('$gte:', ''))
    : null;

  const filterDefaultValue = {
    label: t('task:filter.allTasks'),
    value: TasksFilterValues.allTasks,
  };
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(
    initialSelectedDate ? dayjs(initialSelectedDate) : null,
  );
  const [selectedFilter, setSelectedFilter] = useState<IFilterOption>(
    initialFilter || filterDefaultValue,
  );
  const [searchValue, setSearchValue] = useState<string>(initialSearch);
  const [dateRange, setDateRange] = useState({
    endDate: initialStartDate || `$gte:${dayjs().startOf('isoWeek').toISOString()}`,
    startDate: initialEndDate || `$lte:${dayjs().endOf('isoWeek').toISOString()}`,
  });
  const {
    data: taskList,
    isLoading: isTasksLoading,
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useFetchTasks({
    pageSize: 50,
    relations: ['matter:subject,matterNumber,currency,invoiceableType', 'responsibleUser:fullName'],
    search: searchValue,
    sortBy: ['dueTime:asc', 'subject:asc'],
    isCritical: true,
    withHasMatterAccess: true,
    extraFilters: {
      activityTypeId: `$eq:${SystemConstants.CRITICAL_TASK_ID}`,
      startTime: dateRange.startDate,
      dueTime: dateRange.endDate,
      statusId: getStatusFilterForTasks(selectedFilter?.value),
    },
    fields: [
      'subject',
      'startTime',
      'dueTime',
      'priority',
      'matterId',
      'statusId',
      'responsibleUserId',
    ],
    enabled: true,
  });
  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" />;
    }
    return null;
  };

  const initializeGroup = (displayKey: string, iterDate: dayjs.Dayjs): IGroupedActivities => ({
    displayKey,
    date: iterDate.toDate(),
    items: [],
    amount: 0,
    totalDuration: { value: '0', unit: 'minutes' },
  });

  const calculateTaskDuration = (item: ITask, iterDate: dayjs.Dayjs) => {
    const activityStart = dayjs.max(dayjs(item.startTime), iterDate);
    const activityEnd = dayjs.min(dayjs(item.dueTime), iterDate.endOf('day'));
    return calculateDuration(activityStart.toISOString(), activityEnd.toISOString());
  };

  const updateGroup = (
    group: IGroupedActivities,
    duration: { value: string; unit: 'minutes' | 'hours' },
    item: ITask,
    startDate: dayjs.Dayjs,
    endDate: dayjs.Dayjs,
    iterDate: dayjs.Dayjs,
  ): IGroupedActivities => {
    const newMinutes = convertToMinutes(duration);
    const existingMinutes = convertToMinutes(group.totalDuration);
    const totalMinutes = existingMinutes + newMinutes;

    group.totalDuration =
      totalMinutes < 60
        ? { value: totalMinutes.toString(), unit: 'minutes' }
        : {
            value: `${Math.floor(totalMinutes / 60)}${
              totalMinutes % 60 === 0 ? '' : `:${String(totalMinutes % 60).padStart(2, '0')}`
            }`,
            unit: 'hours',
          };

    group.amount += 1;

    const totalDays = endDate.diff(startDate, 'days') + 1;
    const currentDay = iterDate.diff(startDate, 'days') + 1;

    group.items.push({
      activity: item,
      currentOfTotalDays: totalDays > 1 ? `${currentDay}/${totalDays}` : '',
    });

    group.items.sort(
      (a: { activity: ITask }, b: { activity: ITask }) =>
        dayjs(a.activity.dueTime).valueOf() - dayjs(b.activity.dueTime).valueOf(),
    );

    return group;
  };

  const convertToMinutes = (duration: { value: string; unit: 'minutes' | 'hours' }): number => {
    if (duration.unit === 'hours') {
      const [h, m = 0] = duration.value.split(':').map(Number);
      return h * 60 + m;
    }
    return Number.parseInt(duration.value, 10);
  };
  const groupedMap = (taskList ?? []).reduce(
    (acc, item): Record<string, IGroupedActivities> => {
      const startDate = dayjs(item.startTime).startOf('day');
      const endDate = dayjs(item.dueTime).startOf('day');
      const startOfWeekDayjs = dayjs(dateRange.endDate.replace('$gte:', '')).startOf('day');
      const endOfWeekDayjs = startOfWeekDayjs.add(6, 'day');
      if (endDate.isBefore(startOfWeekDayjs) || startDate.isAfter(endOfWeekDayjs)) {
        return acc; // Skip if out of week range
      }

      let iterDate = startDate;

      while (iterDate.isSameOrBefore(endDate)) {
        if (iterDate.isBefore(startOfWeekDayjs) || iterDate.isAfter(endOfWeekDayjs)) {
          iterDate = iterDate.add(1, 'day');
          continue;
        }

        const dateKey = iterDate.format('YYYY-MM-DD');
        const displayKey = getDisplayKey(dateKey, iterDate, t);

        if (!acc[displayKey]) {
          acc[displayKey] = initializeGroup(displayKey, iterDate);
        }
        const duration = calculateTaskDuration(item, iterDate);
        acc[displayKey] = updateGroup(
          acc[displayKey],
          duration,
          item,
          startDate,
          endDate,
          iterDate,
        );

        iterDate = iterDate.add(1, 'day');
      }

      return acc;
    },
    {} as Record<string, IGroupedActivities>,
  );

  const sortedGrouped = (Object.values(groupedMap) as IGroupedActivities[]).sort(
    (a, b) => a.date.getTime() - b.date.getTime(),
  );
  const { isCollapsed, toggle, expand, getToggleAllControl } = useCollapsedDateGroups(
    DateGroupList.criticalTasks,
  );
  useFetchAllPages({ hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage });
  useEffect(() => {
    setSearchParams({
      transactionSearch: searchValue,
      transactionFilter: selectedFilter?.value || '',
      selectedDate: selectedDate ? selectedDate.format('YYYY-MM-DD') : '',
      startDate: dateRange.endDate,
      endDate: dateRange.startDate,
    });
  }, [searchValue, selectedFilter, selectedDate, dateRange]);

  return (
    <div className="d-flex flex-col h-full">
      <CollapsibleListLayout
        testId="critical-task-listing"
        contentGap={30}
        contentClassName="w-full"
        pinnedHeader={
          <CalendarView
            onWeekChange={(start, end) => {
              setDateRange({
                endDate: `$gte:${dayjs(start).startOf('day').toISOString()}`,
                startDate: `$lte:${dayjs(end).endOf('day').toISOString()}`,
              });
            }}
            onDateChange={(dateObj) => {
              const dateKey = dayjs(dateObj).isSame(dayjs(), 'day')
                ? t('common:today')
                : dayjs(dateObj).format('MMM DD, YYYY');
              expand(toDateGroupKey(dateObj));
              scrollToDate(dateKey);
            }}
            onSelectedDateChange={(date) => {
              setSelectedDate(dayjs(date));
            }}
            groupedMap={groupedMap}
            initialWeekStart={parsedInitialStartDate ? parsedInitialStartDate.toDate() : undefined}
            customDate={initialSelectedDate ? new Date(initialSelectedDate) : undefined}
          />
        }
        header={
          <SearchInputWithFilters
            dateGroupToggle={getToggleAllControl(
              sortedGrouped.map((group) => toDateGroupKey(group.date)),
            )}
            searchTitle={t('task:searchCriticalTasks')}
            searchValue={searchValue}
            filterTitle={t('task:filter.allCriticalTasks')}
            radioOptions={criticalTaskFilterOptions}
            handleSearch={(val: string) => {
              setSearchValue(val);
            }}
            handleRefresh={() => {
              queryClient.removeQueries({ queryKey: ['tasks-list'] });
              return refetch();
            }}
            selectedFilter={[selectedFilter]}
            setSelectedFilter={(val) => setSelectedFilter(val)}
            clearFilter={() => {
              setSelectedFilter(filterDefaultValue);
            }}
            handleAdd={() => {
              updateTask({
                startTime: selectedDate
                  ? dayjs(selectedDate).startOf('day').toISOString()
                  : undefined,
                dueTime: selectedDate
                  ? dayjs(selectedDate).add(1, 'day').startOf('day').toISOString()
                  : undefined,
                alertTime: selectedDate
                  ? dayjs(selectedDate).startOf('day').toISOString()
                  : undefined,
              } as ITask);
              clearInitialTask();
              push(ROUTES.ADD_EDIT_CRITICAL_TASK);
            }}
            tooltipTextForAdd={t('task:criticalTask.addNew')}
          >
            <SortSelector
              modalLabel={t('common:sorting.title')}
              options={calendarSortOptions}
              activeSort={calendarActiveSort}
              disabled
            />
          </SearchInputWithFilters>
        }
      >
        {sortedGrouped &&
          Object.entries(sortedGrouped).length > 0 &&
          sortedGrouped.map((val: IGroupedActivities) => {
            const formattedDate =
              val.displayKey === t('common:today')
                ? val.displayKey
                : formatter.dateStandard(new Date(val.displayKey));
            const groupKey = toDateGroupKey(val.date);
            return (
              <CollapsibleDateGroup
                key={groupKey}
                ref={getDateRef(val.displayKey)}
                className="scroll-anchor"
                title={formattedDate}
                itemCount={t('task:taskCount', { count: val.items.length })}
                total={val.totalDuration.value + (val.totalDuration.unit === 'minutes' ? 'm' : 'h')}
                collapsed={isCollapsed(groupKey)}
                onToggle={() => toggle(groupKey)}
              >
                {val.items.map((task: { activity: ITask; currentOfTotalDays: string }) => {
                  const duration = calculateTaskDuration(
                    task.activity,
                    dayjs(task.activity.startTime).startOf('day'),
                  );
                  return (
                    <CriticalCard
                      key={task.activity.id}
                      time={dayjs(task.activity.startTime).format('HH:mm')}
                      duration={
                        duration.value +
                        (duration.unit === 'minutes' ? ` ${t('common:min')}` : ` ${t('common:h')}`)
                      }
                      title={
                        task?.currentOfTotalDays.length > 0
                          ? `(${t('common:day')} ${task?.currentOfTotalDays}) ${task?.activity?.subject}`
                          : '' + task?.activity?.subject
                      }
                      task={task.activity}
                      user={task.activity?.responsibleUser?.fullName}
                      matter={
                        task.activity?.matter
                          ? task?.activity?.matter?.matterNumber +
                            '-' +
                            task?.activity?.matter?.subject
                          : ''
                      }
                      isCompleted={
                        task.activity?.statusId === SystemConstants.TASK_STATUS_COMPLETED
                      }
                      onClick={() => {
                        push(ROUTES.CRITICAL_TASKS_DETAIL + '/' + task.activity?.id);
                      }}
                    />
                  );
                })}
              </CollapsibleDateGroup>
            );
          })}
        {(!taskList || (Object.entries(taskList).length === 0 && !isTasksLoading)) && (
          <div className="flex-grow d-flex justify-center items-center">
            <NoData header={t('task:noCriticalTasksForThisWeek')} icon={<DangerEmpty />} />
          </div>
        )}
        {isTasksLoading ? (
          <SkeletonGroupedCardList rows={[1]} withSubcontentOnFirstRow withHeader={false} />
        ) : (
          renderLoader()
        )}
      </CollapsibleListLayout>
    </div>
  );
};
export default CriticalTaskLayout;
